'use client'

import Button from 'shared/ui/Button'
import { Modal, ModalTitle } from 'shared/ui/Modal'

import { ValidateCustomerAddressResult } from '../utils'

import styles from './ConfirmAddressModal.module.scss'

interface ConfirmAddressModal {
  open: boolean
  onClose: () => void
  addressValidation: ValidateCustomerAddressResult | null
  onUpdate: (
    key: 'postalCode' | 'city' | 'street' | 'houseNumber' | 'flatNumber',
    value: string
  ) => void
  flatNumber?: string | null
  staircaseNumber?: string | null
}

const ConfirmAddressModal = ({
  open,
  onClose,
  addressValidation,
  onUpdate,
  flatNumber,
  staircaseNumber
}: ConfirmAddressModal) => {
  // given
  const gPart = (key: string) => {
    return addressValidation?.values ? addressValidation.values[key]?.given : ''
  }

  // recommended
  const rPart = (key: string) => {
    if (addressValidation?.values) {
      const value = addressValidation.values[key]?.recommended
      const differences = addressValidation?.differences

      return differences && differences[key] ? (
        <span>
          <span className={styles.highlight}>{value}</span>
        </span>
      ) : (
        <span>{value}</span>
      )
    }

    return ''
  }

  const renderRecommended = () => {
    if (addressValidation?.values) {
      return (
        <>
          {rPart('street')} {rPart('houseNumber')}
          {flatNumber ? `/${flatNumber}` : ''}, {rPart('postalCode')},{' '}
          {rPart('city')}
        </>
      )
    } else {
      return <div />
    }
  }

  const handleSubmit = () => {
    if (addressValidation?.differences) {
      Object.entries(addressValidation.differences)?.forEach(
        ([key, values]) => {
          const keyToUpdate = key as
            | 'postalCode'
            | 'city'
            | 'street'
            | 'houseNumber'
            | 'flatNumber'

          onUpdate(keyToUpdate, values.recommended)
        }
      )
    }
    onClose()
  }

  const hasUnconfirmedAddress = addressValidation?.isAnyUnconfirmed
  const hasNoDeliverySlot = !addressValidation?.hasAnySlot

  return addressValidation ? (
    <Modal id='confirm-address-modal' open={open} onClose={onClose}>
      <ModalTitle>Potwierdź adres</ModalTitle>
      {hasUnconfirmedAddress && (
        <div className={styles.description}>
          Nie możesz zapisać tego adresu, gdyż nie przeszedł pozytywnie
          weryfikacji poprawności.
        </div>
      )}
      {!hasUnconfirmedAddress && hasNoDeliverySlot && (
        <div className={styles.description}>
          Na ten adres obecnie nie dostarczamy.
        </div>
      )}
      <div className={styles.sectionTitle}>Podany adres:</div>
      <div>
        {gPart('street')} {gPart('houseNumber')}
        {staircaseNumber ? `/${staircaseNumber}` : ''}
        {flatNumber ? `/${flatNumber}` : ''}, {gPart('postalCode')},{' '}
        {gPart('city')}
      </div>
      {addressValidation.hasDifferences && (
        <>
          <div className={styles.sectionTitle}>Proponowany:</div>
          <div>{renderRecommended()}</div>
        </>
      )}
      {addressValidation.isAnyUnconfirmed && (
        <>
          <div className={styles.sectionTitle}>
            Fragmenty adresu, których nie udało się potwierdzić:
          </div>
          {Object.values(addressValidation?.unconfirmed || {}).map(
            (unconfirmedComponent, index) => {
              return (
                <div key={`unconfirmed-${index}`}>
                  - {unconfirmedComponent.given}
                </div>
              )
            }
          )}
        </>
      )}
      <div className={styles.description} style={{ marginTop: 24 }}>
        Jeśli podany adres jest prawidłowy, a problem wciąż występuje,{' '}
        <a href='mailto:bok@lokalnyrolnik.pl'>skontaktuj się</a> z Biurem
        Obsługi Klienta.
      </div>

      <div className={styles.buttonsWrapper}>
        <Button variant='neutralOutline' onClick={onClose} fullWidth>
          Anuluj
        </Button>
        <Button variant='primary' onClick={handleSubmit} fullWidth>
          Popraw adres
        </Button>
      </div>
    </Modal>
  ) : (
    <div />
  )
}

export default ConfirmAddressModal
